import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  CheckCircle2, Loader2, Ticket, Calendar, ArrowRight, Sparkles, Download, Share2,
} from 'lucide-react';
import { getBackendUrl } from '../services/origin';
import CookieConsentBanner from '../components/CookieConsentBanner';
import CookieFooterLink from '../components/CookieFooterLink';
import { loadTracking, trackEvent } from '../services/tracking';

const API = getBackendUrl();

/**
 * Success page reached after Stripe redirects back. URL:
 *   `/event/<slug>/checkout/success?o=<order_token>`
 *
 * Polls `/api/public/orders/{order_token}` every 2s until `status=paid`
 * (webhook may be slightly delayed). Once paid, shows tickets with QR
 * codes and — if Lazo opt-in — a CTA to the customer's Lazo dashboard.
 */
export default function EventCheckoutSuccessPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const orderToken = searchParams.get('o') || '';
  const isPaypal = searchParams.get('pp') === '1';
  const paypalOrderId = searchParams.get('token') || '';

  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [tries, setTries] = useState(0);
  const [capturing, setCapturing] = useState(isPaypal && !!paypalOrderId);

  // Fire `purchase` exactly once per order_token (idempotent via sessionStorage)
  useEffect(() => {
    if (!order || order.status !== 'paid' || !order.brand_id) return;
    const fireKey = `lazo_purchase_fired_${orderToken}`;
    try {
      if (sessionStorage.getItem(fireKey)) return;
    } catch { /* ignore */ }
    loadTracking(order.brand_id).then(() => {
      const items = (order.lines || []).map(ln => ({
        item_id: `${order.event_id || ''}:${ln.tier_name || ''}`,
        item_name: ln.tier_name || '',
        item_category: order.event_name || 'Event ticket',
        price: Number(ln.price_each || 0),
        quantity: Number(ln.quantity || 0),
      }));
      trackEvent(order.brand_id, 'purchase', {
        currency: order.currency || 'EUR',
        value: Number(order.total_eur || 0),
        transaction_id: order.order_token,
        event_id: order.order_token,
        items,
      });
      try { sessionStorage.setItem(fireKey, '1'); } catch { /* ignore */ }
    });
  }, [order, orderToken]);

  const fetchOrder = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/public/orders/${orderToken}`);
      setOrder(res.data);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Ordine non trovato');
      return null;
    }
  }, [orderToken]);

  const capturePaypal = useCallback(async () => {
    if (!paypalOrderId) return;
    try {
      await axios.post(`${API}/api/public/event-orders/${paypalOrderId}/capture`);
    } catch (err) {
      // Capture failures are logged server-side; success page will keep polling
      // and surface a problem via the timeout banner if the order never flips.
      console.warn('paypal capture failed', err?.response?.data || err.message);
    } finally {
      setCapturing(false);
    }
  }, [paypalOrderId]);

  useEffect(() => {
    if (!orderToken) {
      setError('Token ordine mancante');
      return;
    }
    let stopped = false;
    const run = async () => {
      if (isPaypal && paypalOrderId) {
        await capturePaypal();
      }
      const tick = async () => {
        if (stopped) return;
        const data = await fetchOrder();
        if (!stopped && data && data.status !== 'paid' && tries < 30) {
          setTries(t => t + 1);
          setTimeout(tick, 2000);
        }
      };
      tick();
    };
    run();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderToken]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center p-6 text-center">
        <p className="text-base font-bold text-[#1A1A1A]">Ordine non trovato</p>
        <p className="text-sm text-[#64748B] mt-1">{error}</p>
        <Link to={`/event/${slug}/checkout`} className="mt-4 text-sm font-semibold text-[#D44E20] underline">
          Torna al checkout
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D44E20]" size={28} />
      </div>
    );
  }

  const isPaid = order.status === 'paid';
  const tickets = order.tickets || [];

  // Honour brand naming everywhere customer-facing (Lazo stays invisible).
  // The success page sets the tab title dynamically to the brand name.
  if (typeof document !== 'undefined' && order?.business_name) {
    document.title = `Biglietti — ${order.business_name}`;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-12" data-testid="event-checkout-success-page">
      <CookieConsentBanner brandId={order?.brand_id} />
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border-b border-emerald-100">
        <div className="max-w-2xl mx-auto px-4 py-10 text-center">
          {isPaid ? (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <CheckCircle2 size={36} className="text-emerald-600" />
              </div>
              <h1 className="text-2xl font-black text-[#1A1A1A]" data-testid="success-title">Pagamento confermato</h1>
              <p className="text-sm text-[#64748B] mt-1">
                I tuoi biglietti per <strong>{order.event_name}</strong> sono pronti.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="animate-spin text-emerald-600 mx-auto mb-3" size={36} />
              <h1 className="text-xl font-bold text-[#1A1A1A]">Conferma in corso…</h1>
              <p className="text-sm text-[#64748B] mt-1">
                Stiamo verificando il pagamento. Non chiudere la pagina.
              </p>
            </>
          )}
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Loyalty CTA (only when opted-in and dashboard exists) */}
        {isPaid && order.lazo_opt_in && order.dashboard_url && (
          <div className="rounded-2xl bg-gradient-to-br from-[#D44E20] to-[#E76A3F] p-5 text-white shadow-lg" data-testid="lazo-cta-card">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Sparkles size={20} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-base">Benvenuto nel {order.program_name || 'programma fedeltà'} di {order.business_name}!</p>
                <p className="text-sm text-white/90 mt-1">
                  Ti abbiamo inviato un SMS con il PIN per accedere alla tua bacheca digitale.
                  Trovi qui i tuoi biglietti, premi e novità.
                </p>
                <a href={order.dashboard_url}
                  className="mt-3 inline-flex items-center gap-2 bg-white text-[#D44E20] px-4 py-2 rounded-xl text-sm font-bold hover:bg-white/95 transition"
                  data-testid="goto-dashboard-btn">
                  Vai alla mia bacheca <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Tickets */}
        {isPaid && tickets.length > 0 && (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.map((t, i) => (
              <TicketCard
                key={t.id}
                ticket={t}
                index={i}
                total={tickets.length}
                eventName={order.event_name}
                eventSlug={order.event_slug}
                eventDate={order.event_date}
                businessName={order.business_name}
              />
            ))}
          </div>
        )}

        {/* Order summary */}
        {isPaid && (
          <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4" data-testid="order-summary-success">
            <h2 className="text-sm font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
              <Calendar size={14} className="text-[#D44E20]" /> Riepilogo ordine
            </h2>
            <div className="space-y-1 text-sm">
              {(order.lines || []).map((l, idx) => (
                <div key={idx} className="flex justify-between">
                  <span className="text-[#475569]">{l.quantity}× {l.tier_name}</span>
                  <span className="text-[#1A1A1A] tabular-nums font-medium">
                    {(l.quantity * l.price_each).toFixed(2).replace('.', ',')} €
                  </span>
                </div>
              ))}
              <div className="border-t border-slate-100 mt-2 pt-2 flex justify-between">
                <span className="font-bold text-[#1A1A1A]">Totale pagato</span>
                <span className="font-black text-[#D44E20] tabular-nums">
                  {Number(order.total_eur || 0).toFixed(2).replace('.', ',')} €
                </span>
              </div>
            </div>
            <p className="text-[11px] text-[#94A3B8] mt-3">
              Una copia dei biglietti è stata inviata a <strong>{order.buyer?.email}</strong>.
            </p>
          </div>
        )}

        {!isPaid && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
            Se questa pagina rimane in attesa per più di 1 minuto, controlla la tua email
            o riprova ad aprire il link di conferma più tardi. Il tuo pagamento è sicuro.
          </div>
        )}

        {/* Discreet footer: cookie management trigger, GDPR art. 7.3 */}
        <div className="mt-8 flex justify-center text-[#94A3B8]">
          <CookieFooterLink />
        </div>
      </main>
    </div>
  );
}


/**
 * Renders one ticket card with TWO actions:
 *   1. "Salva biglietto" — direct download (most reliable cross-platform
 *      way to land the PNG on the user's device; on Android it goes to
 *      /Download, on iOS Safari opens the viewer where the user can
 *      "Salva immagine").
 *   2. "Condividi" — opens the native share sheet with the PNG attached
 *      and a pre-filled invite message + public event URL, so forwarding
 *      via WhatsApp/Telegram/Email also acts as organic word-of-mouth.
 *
 * Why two buttons? Android's share sheet only lists *target apps* (no
 * "Save Image" entry like iOS) — splitting save vs share avoids the
 * confusion the user reported.
 */
function triggerDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function TicketCard({ ticket, index, total, eventName, eventSlug, eventDate, businessName }) {
  const qrRef = useRef(null);
  const [busy, setBusy] = useState(null); // null | 'save' | 'share'

  /** Render the QR + branding canvas, return blob + suggested filename. */
  const composeTicketPng = async () => {
    const svgEl = qrRef.current?.querySelector('svg');
    if (!svgEl) throw new Error('QR non disponibile');
    const svgString = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = svgUrl;
    });

    const W = 800; const H = 1100;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#F8FAFC'; ctx.fillRect(0, 0, W, H);
    const cardX = 40; const cardY = 40; const cardW = W - 80; const cardH = H - 80;
    ctx.fillStyle = '#FFFFFF';
    roundRect(ctx, cardX, cardY, cardW, cardH, 32); ctx.fill();

    const grad = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY);
    grad.addColorStop(0, '#D44E20'); grad.addColorStop(1, '#F4A261');
    ctx.fillStyle = grad;
    roundRectTop(ctx, cardX, cardY, cardW, 90, 32); ctx.fill();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = '700 30px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('🎟️  BIGLIETTO EVENTO', W / 2, cardY + 56);

    ctx.fillStyle = '#1A1A1A';
    ctx.font = '700 36px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    wrapText(ctx, eventName || 'Evento', W / 2, cardY + 170, cardW - 100, 44);

    let yCursor = cardY + 240;
    if (eventDate) {
      ctx.fillStyle = '#64748B';
      ctx.font = '500 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillText(`📅  ${eventDate}`, W / 2, yCursor);
      yCursor += 36;
    }

    const QR_SIZE = 440;
    const qrX = (W - QR_SIZE) / 2; const qrY = yCursor + 20;
    ctx.fillStyle = '#F8FAFC';
    roundRect(ctx, qrX - 16, qrY - 16, QR_SIZE + 32, QR_SIZE + 32, 16); ctx.fill();
    ctx.drawImage(img, qrX, qrY, QR_SIZE, QR_SIZE);
    URL.revokeObjectURL(svgUrl);

    yCursor = qrY + QR_SIZE + 56;
    ctx.fillStyle = '#1A1A1A';
    ctx.font = '700 30px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${ticket.buyer_first_name || ''} ${ticket.buyer_last_name || ''}`.trim() || 'Intestatario', W / 2, yCursor);

    yCursor += 36;
    ctx.fillStyle = '#D44E20';
    ctx.font = '600 22px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(`Biglietto ${index + 1} di ${total} · ${ticket.tier_name || ''}`, W / 2, yCursor);

    yCursor += 56;
    ctx.fillStyle = '#94A3B8';
    ctx.font = '400 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.fillText(`${businessName || 'Mostra questo codice all\'ingresso'}`, W / 2, yCursor);

    const safeName = (eventName || 'evento').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
    const fileName = `biglietto-${safeName}-${index + 1}.png`;
    const blob = await new Promise(res => canvas.toBlob(res, 'image/png', 0.95));
    if (!blob) throw new Error('Impossibile generare immagine');
    return { blob, fileName };
  };

  const handleSave = async () => {
    if (busy) return;
    setBusy('save');
    try {
      const { blob, fileName } = await composeTicketPng();
      triggerDownload(blob, fileName);
    } catch (e) {
      console.error('save ticket failed', e);
      // eslint-disable-next-line no-alert
      alert('Impossibile salvare il biglietto. Riprova o fai uno screenshot.');
    } finally {
      setBusy(null);
    }
  };

  const handleShare = async () => {
    if (busy) return;
    setBusy('share');
    try {
      const { blob, fileName } = await composeTicketPng();
      const file = new File([blob], fileName, { type: 'image/png' });
      // Pre-filled invite text + public event URL → forwarding via
      // WhatsApp/Telegram doubles as word-of-mouth marketing.
      const eventUrl = eventSlug
        ? `${window.location.origin}/event/${eventSlug}/checkout`
        : '';
      const inviteText = [
        `Sto andando a ${eventName || 'questo evento'} 🎉`,
        'Vieni anche tu!',
        eventUrl ? `Biglietti qui: ${eventUrl}` : '',
      ].filter(Boolean).join('\n');

      const canShareFile = typeof navigator !== 'undefined'
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] });
      if (canShareFile) {
        try {
          await navigator.share({
            files: [file],
            title: `Biglietto · ${eventName || ''}`.trim(),
            text: inviteText,
            ...(eventUrl ? { url: eventUrl } : {}),
          });
        } catch (err) {
          // AbortError == user dismissed the sheet — silent.
          if (err && err.name !== 'AbortError') {
            triggerDownload(blob, fileName);
          }
        }
      } else {
        // Desktop / browsers without file-share support → just download.
        triggerDownload(blob, fileName);
      }
    } catch (e) {
      console.error('share ticket failed', e);
      // eslint-disable-next-line no-alert
      alert('Impossibile condividere il biglietto. Riprova o fai uno screenshot.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5" data-testid={`ticket-${index}`}>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">
        <Ticket size={11} /> Biglietto {index + 1} di {total}
      </div>
      <p className="text-base font-bold text-[#1A1A1A] mt-1">{ticket.tier_name}</p>
      <p className="text-xs text-[#64748B]">{ticket.buyer_first_name} {ticket.buyer_last_name}</p>
      <div ref={qrRef} className="mt-3 bg-slate-50 rounded-xl p-4 flex flex-col items-center">
        <QRCodeSVG value={ticket.qr_code} size={180} level="M" />
        <p className="text-[10px] text-[#94A3B8] font-mono mt-2 break-all text-center">
          {ticket.qr_code}
        </p>
      </div>
      <div className="mt-4 grid gap-2">
        <button
          onClick={handleSave}
          disabled={!!busy}
          data-testid={`save-qr-btn-${index}`}
          className="w-full h-14 rounded-2xl bg-[#D44E20] hover:bg-[#ED5E2B] active:bg-[#B94017] text-white text-base font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 shadow-[0_6px_18px_rgba(212,78,32,0.25)]"
        >
          {busy === 'save' ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
          {busy === 'save' ? 'Generazione…' : 'Salva biglietto'}
        </button>
        <button
          onClick={handleShare}
          disabled={!!busy}
          data-testid={`share-qr-btn-${index}`}
          className="w-full h-14 rounded-2xl bg-white border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A]/30 text-[#1A1A1A] text-base font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          {busy === 'share' ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />}
          {busy === 'share' ? 'Generazione…' : 'Condividi'}
        </button>
      </div>
      <p className="text-[11px] text-[#475569] mt-3 text-center">
        Mostra questo QR all'ingresso. Stato: <strong className="text-emerald-700">{ticket.status}</strong>
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Canvas helpers
// ─────────────────────────────────────────────────────────────────────────────
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
function roundRectTop(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || '').split(' ');
  let line = '';
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    if (ctx.measureText(testLine).width > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, y);
      line = words[i] + ' ';
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), x, y);
}
